import React, {memo, useEffect, useRef, useState} from "react"
import { useLocalization } from "../../../../hooks/localization"
import { useSelector } from "react-redux"
import { PasswordContainer } from "../../../../components/PasswordContainer"
import {SettingsPageContainer} from "../../../../components/Settings/SettingsPageContainer"
import { useRPC } from "../../../../hooks/rpc"
import { useMessage } from "../../../../hooks/message"
import './index.scss'; 
import { BusyIndicator } from '../../../../components/BusyIndicator'
import { useQuestion } from '../../../../hooks/question'

type UpdateStatusResponse = {
    data?: string
    params?: Array<{ 
        data?: string
        current_version?: string
        new_version?: string
    }>
}

const UPDATE_STATUS = {
    CHECK_VERSION_UPDATE: "check_version_update",
    NEW_VERSION_AVAILABLE: "new_version_available",
    UPDATE_STATUS_DOWNLOADING: "update_status_downloading",
    UPDATE_STATUS_READY: "update_status_ready",
    UPDATE_SYSTEM: "system_updating",
    SYSTEM_UP_TO_DATE: "system_up_to_date"
} as const

type UpdateStatus = typeof UPDATE_STATUS[keyof typeof UPDATE_STATUS]

interface ButtonStates {
    canCheckVersion: boolean
    canDownload: boolean
    canCheckReady: boolean
    canUpdate: boolean
    isUiLocked: boolean
}

interface VersionInfo {
    currentVersion: string
    newVersion: string
}

export const SettingsUpdateScreen = memo(function SettingsUpdateScreen() {
    const {t} = useLocalization()
    const remoteUpdate = useSelector((state => state.control.remoteUpdate))
    const [rpc] = useRPC()
    const { success, failure } = useMessage()
    const question = useQuestion()
    const [updateStatus, setUpdateStatus] = useState<string>(null)
    const [isChecking, setIsChecking] = useState<boolean>(false)
    const [isDownloading, setIsDownloading] = useState<boolean>(false)
    const [downloadProgress, setDownloadProgress] = useState<number>(0)
    const downloadTimerRef = useRef<any>(null)
    const [statusOverride, setStatusOverride] = useState<UpdateStatus | ''>('')
    const [isCheckingReady, setIsCheckingReady] = useState<boolean>(false)
    const [isUpdatingLocal, setIsUpdatingLocal] = useState<boolean>(false)
    const [versionInfo, setVersionInfo] = useState<VersionInfo | null>(null)


    useEffect(() => {
        rpc.getUpdateStatus()
            .then((res: UpdateStatusResponse) => {
                const direct = (res && typeof res === 'object' && 'data' in res) ? res.data : null
                const viaParams = res?.params?.[0]?.data
                setUpdateStatus(direct || viaParams || null)
            })
            .catch((err) => failure(err))
    }, [rpc, failure, remoteUpdate?.update_status])

    useEffect(() => {
        const current = (remoteUpdate?.update_status || updateStatus || statusOverride) as string
        if (isDownloading) {
            if (current === UPDATE_STATUS.UPDATE_STATUS_READY) {
                setDownloadProgress(100)
                if (downloadTimerRef.current) {
                    clearInterval(downloadTimerRef.current)
                    downloadTimerRef.current = null
                }
                setIsDownloading(false)
                setStatusOverride('')
            }
        } else {
            if (downloadTimerRef.current) {
                clearInterval(downloadTimerRef.current)
                downloadTimerRef.current = null
            }
        }
    }, [statusOverride, updateStatus, remoteUpdate?.update_status, isDownloading])

    useEffect(() => {
        const current = (remoteUpdate?.update_status || '') as string
        if (current !== UPDATE_STATUS.UPDATE_SYSTEM) {
            setIsUpdatingLocal(false)
        }
    }, [remoteUpdate?.update_status])

    useEffect(() => {
        const current = (remoteUpdate?.update_status || statusOverride || updateStatus) as string
        if (current === UPDATE_STATUS.NEW_VERSION_AVAILABLE) {
            if (!versionInfo) {
                rpc.getVersionStatus()
                    .then((res: any) => {
                        
                        if (res && typeof res === 'object') {
                            const currentVersion = res.current_version || ''
                            const newVersion = res.new_version || ''    
                            setVersionInfo({
                                currentVersion,
                                newVersion
                            })
                        } else {
                            setVersionInfo(null)
                        }
                    })
                    .catch((err) => {
                        setVersionInfo(null)
                    })
            }
        } else {
            if (versionInfo) setVersionInfo(null)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [remoteUpdate?.update_status, statusOverride, updateStatus])

    const getButtonStates = (status: UpdateStatus | string): ButtonStates => {
        const baseStates: ButtonStates = {
            canCheckVersion: false,
            canDownload: false,
            canCheckReady: false,
            canUpdate: false,
            isUiLocked: false
        }

        if (isChecking) {
            return { ...baseStates, isUiLocked: true }
        }

        switch (status) {
            case UPDATE_STATUS.CHECK_VERSION_UPDATE:
            case UPDATE_STATUS.SYSTEM_UP_TO_DATE:
                return {
                    ...baseStates,
                    canCheckVersion: true
                }

            case UPDATE_STATUS.NEW_VERSION_AVAILABLE:
                return {
                    ...baseStates,
                    canCheckVersion: true,
                    canDownload: true
                }

            case UPDATE_STATUS.UPDATE_STATUS_DOWNLOADING:
                return {
                    ...baseStates,
                    isUiLocked: true
                }

            case UPDATE_STATUS.UPDATE_STATUS_READY:
                return {
                    ...baseStates,
                    canCheckReady: true,
                    canUpdate: true
                }

            case UPDATE_STATUS.UPDATE_SYSTEM:
                return {
                    ...baseStates,
                    isUiLocked: true
                }

            default:
                return {
                    ...baseStates,
                    canCheckVersion: true
                }
        }
    }

    const onCheckVersion = () => {
        if (isChecking) return
        setIsChecking(true)
        rpc.checkVersion()
            .then(() => {})
            .catch((err) => success(err))
            .finally(() => setIsChecking(false))
    }

    const onDownload = () => {
        if (isDownloading) return
        setIsDownloading(true)
        setStatusOverride(UPDATE_STATUS.UPDATE_STATUS_DOWNLOADING)
        setDownloadProgress(0)

        rpc.downloadNewVersion()
            .then(() => {})
            .catch((err) => failure(err))
        const stepMs = 200
        downloadTimerRef.current = setInterval(() => {
            setDownloadProgress(prev => {
                if (prev >= 95) return 95
                return prev + 1
            })
        }, stepMs)
    }
    
    const onUpdate = () => {
        setIsCheckingReady(true)
        rpc.checkSystemsReady()
            .then(() => {
                setIsCheckingReady(false)
                question(t('this_update_process_will_take_some_time'), () => {
                    setStatusOverride(UPDATE_STATUS.UPDATE_SYSTEM)
                    setIsUpdatingLocal(true)
                    rpc.updateSystem()
                        .then(() => {})
                        .catch((err) => failure(err))
                }, undefined, { text: t('Yes'), timed: false }, false)
            })
            .catch((err) => {
                setIsCheckingReady(false)
                failure(err)
            })
    }

    const effectiveStatus = (remoteUpdate?.update_status || statusOverride || updateStatus || '') as UpdateStatus | string
    const buttonStates = getButtonStates(effectiveStatus)
    const showBusy = isCheckingReady || isUpdatingLocal || effectiveStatus === UPDATE_STATUS.UPDATE_SYSTEM
    const getStatusText = (s: string | null | undefined) => {
        if (!s) return '-'
        const key = `${s}`
        return t(key)
    }

    return(
        <PasswordContainer requiredRole="developer" isFullscreen={true}>
            {/*“Developer” permission was granted for testing, but it should be “service”. (27.10.2025)*/}
            <SettingsPageContainer buttons={[]} className="service-edit" backUrl={"/settings/advanced-settings"}>
                <div className="content-divider">
                    <div className="content-wrapper">
                        <div className="settings-update-container">
                            <div 
                                className={`button ${!buttonStates.canCheckVersion ? " disabled" : ""}`}
                                style={{ opacity: buttonStates.canCheckVersion ? 1 : 0.5 }}
                                onClick={() => { if (buttonStates.canCheckVersion) onCheckVersion() }}
                            >
                                <span className="text">{t("check_version")}</span>
                            </div>
                            <div 
                                className={`button ${!buttonStates.canDownload ? " disabled" : ""}`}
                                style={{ opacity: buttonStates.canDownload ? 1 : 0.5 }}
                                onClick={() => { if (buttonStates.canDownload) onDownload() }}
                            >
                                <span className="text">{t("download_version")}</span>
                            </div>

                            <div 
                                className={`button ${!buttonStates.canUpdate ? " disabled" : ""}`}
                                style={{ opacity: buttonStates.canUpdate ? 1 : 0.5 }}
                                onClick={() => { if (buttonStates.canUpdate) onUpdate() }}
                            >
                                <span className="text">{t("update_system")}</span>
                            </div>
                        </div>
                    </div>

                    <div className="content-wrapper">
                        <div className="status-box">
                             <div className="row">
                                 <span className="label">{t("trigger_status")}:</span> 
                                 <span className="value" data-status={effectiveStatus as string}>{getStatusText(effectiveStatus)}</span>
                            </div>
                            {effectiveStatus === UPDATE_STATUS.NEW_VERSION_AVAILABLE && (
                                <>
                                    <div className="row">
                                        <span className="label">{t("current_version")}:</span>
                                        <span className="value">
                                            {(versionInfo?.currentVersion || '-')}
                                        </span>
                                    </div>
                                    <div className="row">
                                        <span className="label">{t("updated_version")}:</span>
                                        <span className="value">
                                            {(versionInfo?.newVersion || '-')}
                                        </span>
                                    </div>
                                </>
                            )}
                            {isDownloading && (
                                <div className="download-progress">
                                    <div className="bar">
                                        <div className="fill" style={{ width: `${downloadProgress}%` }} />
                                    </div>
                                    <div className="percent">{downloadProgress}%</div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </SettingsPageContainer>
            {showBusy && <BusyIndicator message={isCheckingReady ? t('checking_files') : t('updating')} />}
        </PasswordContainer>
    )
})